import { FormControlLabel, Switch } from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import { SubSectionContent } from '@shared/components/core/layouts/CommonLayoutStyles';
import { BetaLicenseFeatureFlags } from '@shared/constants/FeatureFlagValues';
import AlertsContainer from '@shared/hooks/state/AlertsContainer';
import CurrentUserContainer from '@shared/hooks/state/CurrentUserContainer';
import useFeatureFlagEditor from '@shared/hooks/useFeatureFlagEditor';
import UserPreferencesModel from '@shared/models/users/UserPreferencesModel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FeatureFlagToggle from '../FeatureFlagToggle';

// TODO: consider moving logic to hooks/user/useUserPreferences being careful not to break useFeatureFlagEditor hook
export default function UserPreferencesSection() {
    const { t } = useTranslation();
    const alertsContainer = AlertsContainer.useContainer();
    const currentUserContainer = CurrentUserContainer.useContainer();
    const [isWarningOptOut, setIsWarningOptOut] = useState(
        currentUserContainer?.currentUser?.userPreferences?.isWarningConflictIndicatorsOptOut ?? false
    );
    const initialFeatureFlags = currentUserContainer.currentUser?.userPreferences?.featureFlags ?? [];
    const { featureFlags, updateFeatureFlags, handleExtendedFeatureFlagChange } =
        useFeatureFlagEditor(initialFeatureFlags);

    const handleSaveUserPreferences = async () => {
        try {
            const preferences: UserPreferencesModel = {
                isWarningConflictIndicatorsOptOut: isWarningOptOut,
                featureFlags: featureFlags,
            };

            const result = await currentUserContainer.updateUserPreferences(preferences);

            if (result?.resultType === 'Ok') {
                const featureFlagResult = await handleExtendedFeatureFlagChange();
                if (featureFlagResult.resultType === 'Unexpected') {
                    alertsContainer.showError(t('common:account.error.userPreferences'));
                    return;
                }
                alertsContainer.showSuccess(t('common:account.success.userPreferences'));
            } else {
                alertsContainer.showError(result?.errors?.join(', ') ?? t('common:account.error.userPreferences'));
            }
        } catch (error) {
            console.error('Error saving user preferences:', error);
            alertsContainer.showError(t('common:account.error.unexpected')); // Provide a generic fallback error message
        }
    };
    return (
        <>
            <SubSectionRow
                title={t('common:account.userPreferences.title')}
                description={t('common:account.userPreferences.description')}
            />
            <SubSectionContent>
                <FormControlLabel
                    value="start"
                    control={
                        <Switch
                            checked={isWarningOptOut}
                            onChange={() => {
                                setIsWarningOptOut(!isWarningOptOut);
                            }}
                        />
                    }
                    label={t('common:account.userPreferences.warningOptOut')}
                    labelPlacement="end"
                />
                {currentUserContainer.userBetaFeatureFlags.map((flag) => (
                    <FeatureFlagToggle
                        key={flag}
                        flagName={flag}
                        isEnabled={featureFlags.includes(flag)}
                        onToggle={updateFeatureFlags}
                        label={t(`common:account.userPreferences.${flag}`)}
                    />
                ))}
                <Button onClick={handleSaveUserPreferences}>{t('common:save')}</Button>
            </SubSectionContent>
        </>
    );
}
